import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import {
  getStoredData,
  saveToGlobalState,
  removeSpecificData,
} from "../../utils/localStorage";
import StepFooter from "../../components/step-footer/StepFooter";
import { LocationOn } from "@mui/icons-material";
import "./CadastroViagem.css";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const defaultZoom = 4.8;
const cityZoom = 14;
const mapId = "DEMO_MAP_ID";

const PLACE_FIELDS = [
  "id",
  "displayName",
  "formattedAddress",
  "location",
  "types",
  "addressComponents",
  "shortFormattedAddress",
  "adrFormatAddress",
];

function getCityStateKey(place) {
  const components = getRawAddressComponents(place);

  let city =
    components.find((c) => c.types?.includes("locality"))?.longText ||
    components.find((c) => c.types?.includes("locality"))?.long_name ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.longText ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.long_name ||
    "";

  let state =
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.shortText ||
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.short_name ||
    "";

  // 🔥 fallback forte
  if (!city || !state) {
    const formatted = getFormattedAddress(place);

    const match = formatted.match(/([^,]+)\s-\s([A-Z]{2})/);
    if (match) {
      city = city || match[1];
      state = state || match[2];
    }
  }

  return `${normalizeText(city)}|${normalizeText(state)}`;
}

function getTextValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.text === "string") return value.text;
    if (typeof value.toString === "function") return value.toString();
  }
  if (typeof value.toString === "function") return value.toString();
  return "";
}

function getPlaceName(place) {
  return (
    place?.name ||
    place?.displayName?.text ||
    getTextValue(place?.displayName) ||
    ""
  );
}

function getFormattedAddress(place) {
  return (
    place?.formattedAddress ||
    place?.formatted_address ||
    place?.shortFormattedAddress ||
    ""
  );
}

function getLocationLiteral(location) {
  if (!location) return null;

  const lat =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lng =
    typeof location.lng === "function" ? location.lng() : location.lng;

  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

function getRawAddressComponents(place) {
  return place?.addressComponents || place?.address_components || [];
}

function normalizeAddressComponents(place) {
  const raw = getRawAddressComponents(place);

  return raw.map((component) => ({
    long_name: component.longText ?? component.long_name ?? "",
    short_name: component.shortText ?? component.short_name ?? "",
    types: component.types || [],
  }));
}

function normalizeText(text = "") {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function translatePlaceText(text = "") {
  return text.replace(/ - /g, " - ");
}

function isBrazilPlace(place) {
  const address = getFormattedAddress(place).toLowerCase();
  const components = getRawAddressComponents(place);

  const isBR = components.some((c) => {
    const countryType = c.types?.includes("country");
    const short = (c.shortText || c.short_name || "").toUpperCase();
    const long = (c.longText || c.long_name || "").toLowerCase();
    return (
      (countryType && short === "BR") ||
      long.includes("brasil") ||
      long.includes("brazil")
    );
  });

  return address.includes("brasil") || address.includes("brazil") || isBR;
}

function isCityPlace(place) {
  const types = place?.types || [];
  const name = normalizeText(getPlaceName(place));
  const address = normalizeText(getFormattedAddress(place));

  const cityTypes = [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_3",
  ];

  const blocked = [
    "aeroporto",
    "airport",
    "rodoviaria",
    "rodoviária",
    "estacao",
    "estação",
    "station",
    "terminal",
    "shopping",
    "mall",
    "hospital",
    "escola",
    "universidade",
    "faculdade",
    "condominio",
    "edificio",
    "edifício",
    "avenida",
    "rodovia",
    "estrada",
  ];

  if (types.includes("country")) return false;

  if (blocked.some((w) => name.includes(w) || address.includes(w))) {
    return false;
  }

  return types.some((t) => cityTypes.includes(t));
}

function buildStoredStop(stored, prefix) {
  const name = stored[`cidade_${prefix}`] || "";
  const mainCity = stored[`cidade_${prefix}_main`] || "";
  const searchValue = stored[`search_${prefix}`] || "";

  const lat = Number(stored[`lat_cidade_${prefix}`]);
  const lng = Number(stored[`lng_cidade_${prefix}`]);

  const placeId =
    stored[`place_id_${prefix}`] || stored[`station_${prefix}_place_id`] || "";

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    place_id: placeId,
    name,
    mainCity,
    searchValue,
    position: { lat, lng },
  };
}

function getCityFromAddress(place) {
  const components = getRawAddressComponents(place);

  const city =
    components.find((c) => c.types?.includes("locality"))?.longText ||
    components.find((c) => c.types?.includes("locality"))?.long_name ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.longText ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.long_name ||
    "";

  const state =
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.shortText ||
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.short_name ||
    "";

  const country =
    components.find((c) => c.types?.includes("country"))?.shortText ||
    components.find((c) => c.types?.includes("country"))?.short_name ||
    "";

  if (country !== "BR") return null;

  return city || state;
}

function getCityFromFormattedAddress(address = "") {
  const parts = String(address)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    if (/\s-\s[A-Z]{2}\b/.test(part)) {
      const city = part.split(" - ")[0].trim();
      if (city) return city;
    }
  }

  return null;
}

const getMainCity = (place) => {
  return (
    getCityFromAddress(place) ||
    getCityFromFormattedAddress(getFormattedAddress(place)) ||
    getCityFromFormattedAddress(place?.vicinity || "") ||
    null
  );
};

function createMarkerContent(label) {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.width = "28px";
  el.style.height = "28px";
  el.style.borderRadius = "999px";
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.background = "#1e88e5";
  el.style.color = "#fff";
  el.style.fontSize = "14px";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
  el.style.border = "2px solid #fff";
  return el;
}

function formatLatLng(pos) {
  if (!pos) return "";
  return `Latitude:${pos.lat} | Longitude:${pos.lng}`;
}

function uniqueCities(items) {
  const seen = new Set();

  return items.filter((item) => {
    const place = item.rawPlace || item;

    const key =
      getCityStateKey(place) ||
      normalizeText(item.title) ||
      item.placeId ||
      `${item?.position?.lat}-${item?.position?.lng}`;

    if (!key) return false;
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

async function geocodeCity(query) {
  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode(
      {
        address: `${query}, Brasil`,
        componentRestrictions: { country: "BR" },
      },
      (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]);
        } else {
          resolve(null);
        }
      },
    );
  });
}

async function runTextSearch(query, cityLocation) {
  const { Place } = await window.google.maps.importLibrary("places");

  try {
    const response = await Place.searchByText({
      textQuery: `${query}`,
      language: "pt-BR",
      region: "BR",
      maxResultCount: 20,
      fields: PLACE_FIELDS,
      ...(cityLocation
        ? {
            locationBias: {
              center: cityLocation,
              radius: 50000,
            },
          }
        : {}),
    });

    return response?.places ?? [];
  } catch (error) {
    console.error("Erro searchByText:", error);
    return [];
  }
}

function normalizePlace(place) {
  const p = place;

  return {
    id: p.id || p.resourceName || "",
    place_id: p.id || p.place_id || p.resourceName || "",
    name: getPlaceName(p),
    formattedAddress: getFormattedAddress(p),
    location: p.location || p.geometry?.location || null,
    types: p.types || [],
  };
}

async function searchCities(query) {
  const geocodeResult = await geocodeCity(query);
  const cityLocation = geocodeResult?.geometry?.location || null;

  const variants = [`${query}`, `${query}, Brasil`, `${query} Brasil`];

  const textResultsNested = await Promise.all(
    variants.map((q) => runTextSearch(q, cityLocation)),
  );

  const textResults = textResultsNested.flat();

  const merged = uniqueCities([
    ...(geocodeResult ? [geocodeResult] : []),
    ...textResults,
  ]);

  const cleaned = merged.filter(
    (place) => isCityPlace(place) && isBrazilPlace(place),
  );

  const hydrated = await Promise.all(
    cleaned.map(async (place) => {
      try {
        if (typeof place.fetchFields === "function") {
          await place.fetchFields({ fields: PLACE_FIELDS });
        }
        return place;
      } catch (e) {
        console.error("fetchFields falhou:", e);
        return place;
      }
    }),
  );

  const mapped = hydrated
    .map((place) => {
      const p = normalizePlace(place);
      const position =
        getLocationLiteral(place.location) ||
        getLocationLiteral(place.geometry?.location) ||
        null;

      const title =
        getTextValue(p.name) ||
        getCityFromAddress(place) ||
        getCityFromFormattedAddress(getFormattedAddress(place)) ||
        "";

      const subtitle = translatePlaceText(getTextValue(p.formattedAddress));

      return {
        rawPlace: place,
        placeId: p.place_id,
        title,
        subtitle,
        position,
        types: p.types,
      };
    })
    .filter((item) => Boolean(item.position) && Boolean(item.title));

  return mapped;
}

function OpcaoMoto() {
  const navigate = useNavigate();

  const [mapHeight, setMapHeight] = useState(350);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalQuery, setModalQuery] = useState("");
  const [modalResults, setModalResults] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 935);
  const [step, setStep] = useState("origem");
  const [searchValue, setSearchValue] = useState("");
  const [searchByStep, setSearchByStep] = useState({
    origem: "",
    destino: "",
  });

  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(true);
  const [selectedOriginStation, setSelectedOriginStation] = useState(null);
  const [selectedDestinationStation, setSelectedDestinationStation] =
    useState(null);
  const [directions, setDirections] = useState(null);

  const [originPos, setOriginPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const mapRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const searchRequestIdRef = useRef(0);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  useEffect(() => {
    const updateMapHeight = () => {
      if (window.innerWidth >= 935) return;

      // distância do topo da tela até o mapa
      const mapElement = document.querySelector(".map-side");

      if (!mapElement) return;

      const top = mapElement.getBoundingClientRect().top;

      // espaço para os botões e margem inferior
      const bottomSpace = 70;

      setMapHeight(window.innerHeight - top - bottomSpace);
    };

    updateMapHeight();

    window.addEventListener("resize", updateMapHeight);

    return () => window.removeEventListener("resize", updateMapHeight);
  }, [step]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 935);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;

    const query = modalQuery.trim();

    if (!query || query.length < 2 || !window.google) {
      setModalResults([]);
      return;
    }

    let cancelled = false;

    const timeout = setTimeout(async () => {
      setModalLoading(true);

      try {
        const results = await searchCities(query);

        if (cancelled) return;

        setModalResults(results.slice(0, 8));
      } catch (e) {
        setModalResults([]);
      } finally {
        setModalLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [modalQuery, isModalOpen]);

  useEffect(() => {
    if (!originPos || !destinationPos || !window.google) return;

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(
      {
        origin: originPos,
        destination: destinationPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          console.error("Erro ao gerar rota:", status);
          setDirections(null);
        }
      },
    );
  }, [originPos, destinationPos]);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        window.google?.maps?.event?.trigger(mapRef.current, "resize");

        if (originPos) {
          mapRef.current.setCenter(originPos);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [originPos]);

  useEffect(() => {
    if (!isLoaded || !window.google || hasRestoredRef.current) return;

    const stored = getStoredData();

    const restoredOrigin = buildStoredStop(stored, "origem");
    const restoredDestination = buildStoredStop(stored, "destino");

    const restoredSearchByStep = {
      origem:
        restoredOrigin?.searchValue ||
        stored.search_origem ||
        restoredOrigin?.mainCity ||
        restoredOrigin?.name ||
        "",
      destino:
        restoredDestination?.searchValue ||
        stored.search_destino ||
        restoredDestination?.mainCity ||
        restoredDestination?.name ||
        "",
    };

    setSearchByStep(restoredSearchByStep);

    if (restoredOrigin) {
      setSelectedOriginStation(restoredOrigin);
      setOriginPos(restoredOrigin.position);
    }

    if (restoredDestination) {
      setSelectedDestinationStation(restoredDestination);
      setDestinationPos(restoredDestination.position);
    }

    const storedStep = stored.current_step;

    if (storedStep === "origem" || storedStep === "destino") {
      setStep(storedStep);
      setSearchValue(restoredSearchByStep[storedStep] || "");
      setMapCenter(
        storedStep === "origem"
          ? restoredOrigin?.position || defaultCenter
          : restoredDestination?.position ||
              restoredOrigin?.position ||
              defaultCenter,
      );
    } else if (restoredDestination) {
      setStep("destino");
      setSearchValue(restoredSearchByStep.destino);
      setMapCenter(restoredDestination.position);
    } else if (restoredOrigin) {
      setStep("origem");
      setSearchValue(restoredSearchByStep.origem);
      setMapCenter(restoredOrigin.position);
    }

    hasRestoredRef.current = true;
  }, [isLoaded]);

  useEffect(() => {
    const query = searchValue.trim();

    if (!shouldSearch) return;

    if (!query || query.length < 2 || !window.google) {
      if (query.length < 2) setStations([]);
      return;
    }

    let cancelled = false;
    const requestId = ++searchRequestIdRef.current;

    const timeout = setTimeout(async () => {
      setLoading(true);

      try {
        const results = await searchCities(query);

        if (cancelled || requestId !== searchRequestIdRef.current) return;

        const normalized = results
          .map((place) => ({
            rawPlace: place.rawPlace || place,
            placeId: place.placeId || place.place_id || place.id || "",
            title: translatePlaceText(place.title || getPlaceName(place) || ""),
            subtitle: translatePlaceText(
              place.subtitle || getFormattedAddress(place) || "",
            ),
            position:
              place.position ||
              getLocationLiteral(place?.location || place?.geometry?.location),
            types: place.types || [],
          }))
          .filter((item) => Boolean(item.position) && Boolean(item.title));

        setStations(normalized.slice(0, 5));
      } catch (error) {
        if (!cancelled) setStations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchValue, shouldSearch, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } =
        await window.google.maps.importLibrary("marker");

      if (cancelled) return;

      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
        originMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
        destinationMarkerRef.current = null;
      }

      const map = mapRef.current;

      const showOriginMarker =
        step === "origem" ? !!originPos : !!originPos && !!destinationPos;

      if (showOriginMarker) {
        originMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: originPos,
          title: "Origem",
          content: createMarkerContent("A"),
        });
      }

      if (destinationPos) {
        destinationMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: destinationPos,
          title: "Destino",
          content: createMarkerContent("B"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, originPos, destinationPos, step]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;

      // origem e destino escolhidos
      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });

        return;
      }

      // etapa origem
      if (step === "origem") {
        if (originPos) {
          map.setCenter(originPos);
          map.setZoom(cityZoom);
        } else {
          map.setCenter(defaultCenter);
          map.setZoom(defaultZoom);
        }

        return;
      }

      // etapa destino
      if (step === "destino") {
        if (!destinationPos) {
          map.setCenter(defaultCenter);
          map.setZoom(defaultZoom);
          return;
        }

        map.setCenter(destinationPos);
        map.setZoom(cityZoom);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isLoaded, originPos, destinationPos, step]);

  async function getPlaceDetailsFromPrediction(placePrediction) {
    const place = placePrediction.toPlace();

    await place.fetchFields({
      fields: PLACE_FIELDS,
    });

    return {
      place_id: place.id,
      name: getPlaceName(place),
      formatted_address: getFormattedAddress(place),
      geometry: {
        location: getLocationLiteral(place.location),
      },
      types: place.types || [],
      address_components: normalizeAddressComponents(place),
    };
  }

  const persistStepSearch = (value) => {
    const key = step === "origem" ? "search_origem" : "search_destino";

    setSearchByStep((prev) => ({
      ...prev,
      [step]: value,
    }));

    saveToGlobalState({
      [key]: value,
    });
  };

  const handleSelectStation = async (station) => {
    try {
      const place = station.rawPlace
        ? station.rawPlace
        : await getPlaceDetailsFromPrediction(station.placePrediction);

      if (!isCityPlace(place)) {
        alert("Selecione apenas cidades válidas.");
        return;
      }

      if (!isBrazilPlace(place)) {
        alert("Selecione apenas cidades do Brasil.");
        return;
      }

      const pos =
        station.position ||
        getLocationLiteral(place.geometry?.location || place.location);
      const mainCity = getMainCity(place);

      const stationPayload = {
        place_id: place.place_id || place.id || station.placeId,
        name: place.name || getPlaceName(place) || station.title,
        formatted_address:
          place.formatted_address ||
          place.formattedAddress ||
          station.subtitle ||
          "",
        mainCity,
        position: pos,
      };

      const nextSearchValue = mainCity || station.title || "";

      if (step === "origem") {
        setSelectedOriginStation(stationPayload);
        setOriginPos(pos);
        setMapCenter(pos);
        setSearchValue(nextSearchValue);
        setSearchByStep((prev) => ({ ...prev, origem: nextSearchValue }));

        saveToGlobalState({
          current_step: "origem",
          place_id_origem: stationPayload.place_id,
          cidade_origem: stationPayload.name,
          cidade_origem_main: mainCity,
          search_origem: nextSearchValue,
          lat_cidade_origem: pos?.lat,
          lng_cidade_origem: pos?.lng,
        });
      } else {
        setSelectedDestinationStation(stationPayload);
        setDestinationPos(pos);
        setMapCenter(pos);
        setSearchValue(nextSearchValue);
        setSearchByStep((prev) => ({ ...prev, destino: nextSearchValue }));

        saveToGlobalState({
          current_step: "destino",
          place_id_destino: stationPayload.place_id,
          cidade_destino: stationPayload.name,
          cidade_destino_main: mainCity,
          search_destino: nextSearchValue,
          lat_cidade_destino: pos?.lat,
          lng_cidade_destino: pos?.lng,
        });
      }

      setShouldSearch(false);
    } catch (e) {
      console.error(e);
      alert("Não foi possível selecionar essa cidade.");
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    setShouldSearch(true);
    setSearchValue(value);
    persistStepSearch(value);

    if (step === "origem") {
      setSelectedOriginStation(null);

      saveToGlobalState({
        cidade_origem: "",
        cidade_origem_main: "",
        search_origem: "",
        lat_cidade_origem: "",
        lng_cidade_origem: "",
      });
    } else {
      setSelectedDestinationStation(null);

      saveToGlobalState({
        cidade_destino: "",
        cidade_destino_main: "",
        search_destino: "",
        lat_cidade_destino: "",
        lng_cidade_destino: "",
      });
    }
  };

  const handleBack = () => {
    if (step === "origem") {
      const data = getStoredData();

      const isSim = data?.voltando_trajeto === "sim";

      if (!isSim) {
        saveToGlobalState({
          voltando_trajeto: "nao",
        });
      }

      if (data?.editar_trajeto === "sim") {
        removeSpecificData("cidade_origem");
        removeSpecificData("cidade_origem_main");
        removeSpecificData("cidade_destino");
        removeSpecificData("cidade_destino_main");
        removeSpecificData("cidades_trajeto");
        removeSpecificData("current_step");
        removeSpecificData("lat_cidade_origem");
        removeSpecificData("lng_cidade_origem");
        removeSpecificData("lat_cidade_destino");
        removeSpecificData("lng_cidade_destino");
        removeSpecificData("horas_ida_viagem");
        removeSpecificData("horas_ida_destino");
        removeSpecificData("origemHora");
        removeSpecificData("destinoHora");
        removeSpecificData("origemData");
        removeSpecificData("destinoData");
        removeSpecificData("lat_cidade_origem");
        removeSpecificData("lng_cidade_origem");
        removeSpecificData("lat_cidade_destino");
        removeSpecificData("lng_cidade_destino");
        removeSpecificData("place_id_origem");
        removeSpecificData("place_id_destino");
        removeSpecificData("search_origem");
        removeSpecificData("search_destino");
        removeSpecificData("dia_mes_ano_ida_viagem");
        removeSpecificData("dia_mes_ano_ida_viagem_para_busca");
        removeSpecificData("dia_mes_ano_ida_destino");
        removeSpecificData("dia_mes_ano_ida_destino_para_busca");
        removeSpecificData("editar_trajeto");
        removeSpecificData("id");
        removeSpecificData("pais_origem");
        removeSpecificData("pais_destino");
        removeSpecificData("select_espaco_mala");
        removeSpecificData("qual_meio_transporte");
        removeSpecificData("voltando_trajeto");
        removeSpecificData("informacao_a_viagem");
        removeSpecificData("viagem_step");
        navigate("/dashboard-traveler");
        return;
      }

      removeSpecificData("qual_meio_transporte");

      navigate(isSim ? "/trips-progress-traveler" : "/qual-meio-transporte");
      return;
    }

    setStep("origem");
    setMapCenter(defaultCenter);

    if (mapRef.current) {
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(defaultZoom);
    }
    setSearchValue(
      searchByStep.origem ||
        selectedOriginStation?.mainCity ||
        selectedOriginStation?.name ||
        "",
    );
    setShouldSearch(true);
    setStations([]);
    saveToGlobalState({ current_step: "origem" });
  };

  const handleNext = () => {
    if (step === "origem") {
      if (!selectedOriginStation) {
        alert("Selecione uma cidade primeiro.");
        return;
      }

      setStep("destino");
      setSearchValue(
        searchByStep.destino ||
          selectedDestinationStation?.mainCity ||
          selectedDestinationStation?.name ||
          "",
      );
      setMapCenter(defaultCenter);

      if (mapRef.current) {
        mapRef.current.setCenter(defaultCenter);
        mapRef.current.setZoom(defaultZoom);
      }
      setShouldSearch(true);
      setStations([]);
      saveToGlobalState({ current_step: "destino" });
      return;
    }

    if (!selectedDestinationStation) {
      alert("Selecione uma cidade de destino primeiro.");
      return;
    }

    saveToGlobalState({ current_step: "destino" });
    navigate("/trajeto");
  };

  const isSelected = (station) => {
    const selected =
      step === "origem" ? selectedOriginStation : selectedDestinationStation;

    if (!selected) return false;

    if (selected.place_id && station.placeId) {
      return selected.place_id === station.placeId;
    }

    const a = selected.position;
    const b = station.position;

    if (a && b) {
      return a.lat === b.lat && a.lng === b.lng;
    }

    return false;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter" || e.repeat) return;

      const tag = e.target.tagName;

      if (tag === "BUTTON") return;

      e.preventDefault();

      if (step === "origem") {
        handleNext();
      } else {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, handleNext]);

  return (
    <>
      <div className="page-shell">
        <div className="main-container">
          <div className="content-side">
            <h1>
              {step === "origem"
                ? "QUAL CIDADE DE ORIGEM?"
                : "QUAL A CIDADE DE DESTINO?"}
            </h1>

            <input
              type="search"
              className="search-input"
              placeholder="Digite a cidade..."
              value={searchValue}
              onChange={handleInputChange}
              onFocus={() => {
                if (!isLargeScreen) {
                  setIsModalOpen(true);
                  setModalQuery(searchValue);
                }
              }}
            />

            {isLargeScreen && (
              <div className="results-card">
                <div className="airport-list">
                  {loading && (
                    <div className="spinner-container">
                      <div className="loading-spinner"></div>
                    </div>
                  )}

                  {stations.map((station) => (
                    <button
                      key={
                        station.placeId ||
                        station.title ||
                        `${station.position?.lat}-${station.position?.lng}`
                      }
                      className={`airport-item ${
                        isSelected(station) ? "selected" : ""
                      }`}
                      onClick={() => handleSelectStation(station)}
                      type="button"
                    >
                      <strong>{station.title}</strong>
                      <div>{station.subtitle}</div>
                      {station.position && (
                        <small style={{ display: "none" }}>
                          {formatLatLng(station.position)}
                        </small>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="actions-row">
              <button className="btn-back" onClick={handleBack} type="button">
                Voltar
              </button>

              <button className="btn-next" onClick={handleNext} type="button">
                {step === "origem" ? "Próximo" : "Avançar"}
              </button>
            </div>
          </div>

          <div
            className="map-side"
            style={
              !isLargeScreen
                ? {
                    height: `${mapHeight}px`,
                    minHeight: `${mapHeight}px`,
                  }
                : undefined
            }
          >
            {isLoaded && (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: isLargeScreen ? "100%" : `${mapHeight}px`,
                }}
                center={mapCenter}
                zoom={defaultZoom}
                options={{ mapId }}
                onLoad={(map) => {
                  mapRef.current = map;

                  // origem e destino
                  if (originPos && destinationPos) {
                    const bounds = new window.google.maps.LatLngBounds();
                    bounds.extend(originPos);
                    bounds.extend(destinationPos);

                    setTimeout(() => {
                      map.fitBounds(bounds);
                    }, 100);

                    return;
                  }

                  // origem
                  if (step === "origem" && originPos) {
                    map.setCenter(originPos);
                    map.setZoom(cityZoom);
                    return;
                  }

                  // destino escolhido
                  if (step === "destino" && destinationPos) {
                    map.setCenter(destinationPos);
                    map.setZoom(cityZoom);
                    return;
                  }

                  // estado inicial
                  map.setCenter(defaultCenter);
                  map.setZoom(defaultZoom);
                }}
              >
                {directions && (
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: "#1e88e5",
                        strokeWeight: 4,
                        strokeOpacity: 0.8,
                      },
                    }}
                  />
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && !isLargeScreen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                background: "linear-gradient(90deg,#1a7fe7,#19c4c9)",

                padding: 25,
                fontWeight: 800,
                color: "#fff",
              }}
            >
              {step === "origem" ? "CIDADE QUE IRÁ SAIR?" : "QUAL O DESTINO?"}
            </div>
            <button
              type="button"
              className="mobile-register-city-close"
              style={{ marginleft: "10px", marginRight: "10px" }}
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>
            <div
              style={{
                display: "flex",
                marginTop: 30,
                padding: 16,
                paddingBottom: 8,
                borderBottom: "1px solid #ddd",
              }}
            >
              <input
                type="search"
                autoFocus
                value={modalQuery}
                onChange={(e) => setModalQuery(e.target.value)}
                placeholder="Pesquisar cidade..."
                className="search-input"
              />
            </div>

            <div style={{ boxShadow: "none" }} className="results-card">
              <div className="airport-list">
                {modalLoading && (
                  <div className="spinner-container">
                    <div className="loading-spinner"></div>
                  </div>
                )}

                {modalResults.map((station) => (
                  <button
                    key={station.placeId}
                    className={`airport-item ${
                      isSelected(station) ? "selected" : ""
                    }`}
                    onClick={() => {
                      handleSelectStation(station);
                      setIsModalOpen(false);
                    }}
                    type="button"
                    style={{
                      borderRadius: "18px",
                      display: "flex",
                      width: "97%",
                      margin: "auto",
                      boxShadow: "0 5px 18px rgba(0,0,0,.08)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <LocationOn
                        sx={{
                          color: "#11b9a3",
                          mr: 2,
                        }}
                      />
                    </div>
                    <div>
                      <strong>{station.title}</strong>
                      <div>{station.subtitle}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <StepFooter />
    </>
  );
}

export default OpcaoMoto;
